import { ArrowPathIcon, BoltIcon } from "@heroicons/react/24/outline";
import { useI18N, useResponsive } from "@sundae/react-hooks";
import { Badge, Balance, Tooltip } from "@sundae/ui-toolkit";
import { EContractVersion } from "@sundaeswap/core";
import divide from "lodash/divide";
import { FC } from "react";
import { joyrideSelectors } from "../../constants/joyride.constants";
import { PoolFragmentFragment } from "../../gql/generated/stats2.sdk";
import { useMarketOpenForPool } from "../../hooks/liquidity/useMarketOpenForPool";
import { useAdaData } from "../../hooks/useAdaData";
import MarketOpenTooltip from "../../i18n/components/liquidity/MarketOpenTooltip";
import { featureIsEnabled } from "../../utils/features.utils";
import { percentageI18n } from "../../utils/number-format";
import { calculateTVLOfPoolInADA } from "../../utils/pool.utils";
import { truncateString } from "../../utils/string-format";

export interface IRenderedBadgesConfig {
  ident?: boolean;
  migrate?: boolean;
  lpFee?: boolean;
  marketOpenStatus?: boolean;
  tvl?: boolean;
  version?: boolean;
}

interface IPoolBadgesProps {
  badges?: IRenderedBadgesConfig;
  pool: PoolFragmentFragment;
}

const defaultBadgesConfig: IRenderedBadgesConfig = {
  ident: true,
  lpFee: true,
  marketOpenStatus: false,
  tvl: false,
  version: featureIsEnabled("v3Contracts"),
};

const PoolBadges: FC<IPoolBadgesProps> = ({ badges, pool }) => {
  const { currentLocale, t } = useI18N("exchange");
  const { data: adaData, isLoading: isLoadingAdaData } = useAdaData();
  const responsive = useResponsive();
  const { formattedMarketOpenDate, isMarketOpen /** marketOpenSlot */ } =
    useMarketOpenForPool(pool);
  const { ident, fee, version = EContractVersion.V1 } = pool;
  const isV1Protocol = version === EContractVersion.V1 || !version; // TODO: remove check for !version once v3 contracts are deployed;
  const badgeSize = "2xs";
  const badgesConfig = {
    ...defaultBadgesConfig,
    ...badges,
  };

  return (
    <div className="flex flex-wrap items-center gap-1">
      {badgesConfig.version && featureIsEnabled("v3Contracts") && (
        <Tooltip
          trigger={
            <div
              data-guide-selector={
                isV1Protocol
                  ? joyrideSelectors.poolBadges.version.v1
                  : joyrideSelectors.poolBadges.version.v3
              }
            >
              <Badge size={badgeSize} variant={isV1Protocol ? "error" : "secondary"}>
                {!isV1Protocol && <BoltIcon className="mr-[2px] h-3 w-3 text-secondary" />}
                {version}
              </Badge>
            </div>
          }
          tooltipLabel={t("version", { ns: "glossary", context: "stable" })}
        />
      )}

      {badgesConfig.lpFee && (
        <Tooltip
          trigger={
            <div>
              <Badge
                size={badgeSize}
                variant={featureIsEnabled("v3Contracts") ? "muted" : "primary"}
              >
                {percentageI18n(divide(Number(fee), 100) || 0, currentLocale)}
              </Badge>
            </div>
          }
          tooltipLabel={t("fee", {
            context: responsive.xl ? "lp" : "lpAbbr",
            ns: "fees",
          })}
        />
      )}

      {badgesConfig.ident && (
        <Tooltip
          primitiveProps={{
            content: { className: "break-words" },
          }}
          trigger={
            <div>
              <Badge size={badgeSize} variant="silent">
                {featureIsEnabled("v3Contracts") ? truncateString(ident, 0, 2) : ident}
              </Badge>
            </div>
          }
          tooltipLabel={t("pool", { context: "withIdent", ident, ns: "glossary" })}
        />
      )}

      {badgesConfig.tvl && (
        <Tooltip
          trigger={
            <div>
              <Badge size={badgeSize} variant="success">
                {isLoadingAdaData ? (
                  <ArrowPathIcon className="h-3 w-3 text-success motion-safe:animate-spin" />
                ) : (
                  <Balance
                    showSymbol
                    locale={currentLocale}
                    size="2xs"
                    variant="success"
                    value={calculateTVLOfPoolInADA(pool, adaData?.usd ?? 0)}
                  />
                )}
              </Badge>
            </div>
          }
          tooltipLabel={t("tvl", { ns: "glossary" })}
        />
      )}

      {badgesConfig.marketOpenStatus && !isMarketOpen && featureIsEnabled("v3Contracts") && (
        <MarketOpenTooltip openDate={formattedMarketOpenDate}>
          <Badge size={badgeSize} variant="primary">
            {t("pool", {
              ns: "glossary",
              context: responsive.lg ? "opens" : "opensShort",
              date: formattedMarketOpenDate,
            })}
          </Badge>
        </MarketOpenTooltip>
      )}
    </div>
  );
};

export default PoolBadges;
